import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事になるには（route=/butler/become）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /butler と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 創立1890年（明治23年）/ 法人設立2025年2月19日 / 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格 / 通信教育プログラムが存在
// 創作した年号・数値・受講料・合格率・輩出人数は書かない

export default function ButlerBecomePage() {
  useEffect(() => {
    updateSEO(seoData['/butler/become']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '執事という仕事', href: '/butler' },
    { label: '執事になるには' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const aptitudePillars = [
    {
      en: 'Observation',
      ja: '観察と先回り',
      text: '言葉になる前の気配を読み、場の流れを静かに整える力。執事の奉仕は「頼まれてから動く」のではなく、必要が顕在化する手前で備えることに本質があります。'
    },
    {
      en: 'Discretion',
      ja: '守秘と節度',
      text: '家庭や組織の最も内側に入る以上、見聞きしたことを外へ持ち出さない姿勢が前提です。沈黙の品位と、必要な報告だけを的確に上げる判断が求められます。'
    },
    {
      en: 'Composure',
      ja: '落ち着きと礼節',
      text: '予期せぬ事態でも声のトーンと所作を崩さないこと。英国式の規律と、日本の細やかな礼を重ねた立ち居振る舞いが、信頼の土台になります。'
    },
    {
      en: 'Stewardship',
      ja: '整える責任感',
      text: '住まい・日程・食卓・人の動きまでを俯瞰し、主人と家族が本来向き合うべき事柄に集中できる環境を保つ。管理ではなく「守り支える」意識が軸です。'
    }
  ];

  const learningEntries = [
    {
      no: '01',
      title: '通信教育プログラム',
      lead: '学びの第一歩を、生活リズムを保ったまま',
      body: '全日本執事協会では、通信教育プログラムを入口のひとつとして用意しています。基礎理論、礼儀作法の考え方、家政と接遇の枠組みを、遠方や多忙な方でも段階的に学べる設計です。独習で終わらせず、課題を通じて「なぜその手順なのか」を言語化する力を養います。',
      href: '/qualifications/distance-learning',
      linkLabel: '通信教育プログラムを見る'
    },
    {
      no: '02',
      title: '対面研修',
      lead: '身体で覚える所作と、場の空気の読み方',
      body: '礼装の扱い、案内の動線、食卓サービス、報告の仕方など、文字だけでは伝わりにくい領域は対面研修で磨きます。仲間とのロールプレイを通じ、声量・間・視線の高さを整え、「いてほしいのに目立たない」立ち位置を体得していきます。',
      href: '/qualifications',
      linkLabel: '資格制度の全体像へ'
    }
  ];

  const associatePoints = [
    '基本的な礼節と身だしなみ、言葉遣いの土台',
    '主人・来客への案内、取次、席次の初歩',
    '家政の全体像を理解し、指示を正確に実行する姿勢',
    '守秘義務と職業倫理の入門',
    '報告・連絡の型と、記録を残す習慣'
  ];

  const professionalPoints = [
    '複数業務を並行しても品質を落とさない段取り力',
    '食卓・接遇・旅程など、現場判断を伴う実務への対応',
    '部下や外部業者との調整を含む、静かなリーダーシップ',
    '主人の嗜好と家の流儀を踏まえた提案型の奉仕',
    '専門資格で得た知見を、日常オペレーションへ統合する力'
  ];

  const masterPoints = [
    '家全体・組織全体を見渡す統括と人材育成',
    '危機時にも品位を保ち、方針を示して場を安定させる力',
    '伝統と現代の生活様式を橋渡しする制度設計への関与',
    '後進の模範となる職業観と、継続的な研鑽の姿勢',
    '協会が掲げる執事像を、実務の最前線で体現すること'
  ];

  const specialtyQuals = [
    { ja: 'ビジネスマナー', href: '/qualifications/business-manner', en: 'Business Manner' },
    { ja: 'ダイニングコーディネーター', href: '/qualifications/dining-coordinator', en: 'Dining' },
    { ja: 'ワインスチュワード', href: '/qualifications/wine-steward', en: 'Wine' },
    { ja: 'ティーソムリエ', href: '/qualifications/tea-sommelier', en: 'Tea' },
    { ja: 'アフタヌーンティー', href: '/qualifications/afternoon-tea', en: 'Afternoon Tea' },
    { ja: 'バトラースタイル・ユニフォーム', href: '/qualifications/butler-style-uniform', en: 'Uniform' },
    { ja: 'イベントプランニング', href: '/qualifications/event-planning', en: 'Events' },
    { ja: 'ハウスホールド・マネージャー', href: '/qualifications/household-manager', en: 'Household' },
    { ja: 'トラベルコンシェルジュ', href: '/qualifications/travel-concierge', en: 'Travel' }
  ];

  const faqItems = [
    {
      q: '年齢や学歴に制限はありますか？',
      a: '当協会の資格ルートでは、年齢・性別・学歴による制限を設けていません。大切なのは、奉仕の精神と学び続ける意志、そして現場で信頼を積み重ねる姿勢です。'
    },
    {
      q: 'どの資格から始めるのがよいですか？',
      a: '多くの方は初級のAssociate Butlerから段階を踏まれます。並行して通信教育プログラムで理論を固め、必要に応じてワインや紅茶、邸宅管理などの専門資格で得意領域を伸ばす進め方が一般的です。'
    },
    {
      q: '資格取得後はどのような研鑽が求められますか？',
      a: '資格は到達点ではなく、実務品質を保つための起点です。現場での振り返りに加え、専門資格の追加取得や協会の学びの場への参加を通じて、継続的に技能と人間性を磨いていきます。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事になるには｜全日本執事協会の資格ルート',
    description:
      '全日本執事協会が示す、執事になるまでの道筋。求められる素養、通信教育と対面研修、Associate・Professional・Masterの三段階と専門資格、取得後の実務と継続研鑽。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/butler/become'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '執事という仕事', item: 'https://japanbutlerassociation.com/butler' },
      { '@type': 'ListItem', position: 3, name: '執事になるには', item: 'https://japanbutlerassociation.com/butler/become' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード（左テキスト／右余白に写真が透ける） */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-butler-profession-hero.jpg"
          alt="品格ある空間で静かに控える執事の姿。奉仕の道の起点を象徴する一枚"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Path to Becoming a Butler
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事になるには</span>
              <span className="block text-[#d7b779]">協会が示す資格の道筋</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創立以来、日本における執事文化の継承と人材育成に取り組んできました。代表理事・石井裕一（三代目頭取）のもと、初級Associate・中級Professional・上級Masterの三段階と専門資格を正典とし、「執事になるまでの道」を体系的に示しています。年齢・性別・学歴による制限は設けていません。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* 本文 — 単一カラムのエディトリアル構成（サイドナビ型は使わない） */}
      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 道筋の俯瞰 — 横ステップ帯（overviewの定義リスト／タイムラインとは別型） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              I. The Route
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              当協会が正典とする<span className="text-[#a38e65]">資格ルート</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              <p>
                執事は、一夜にして成る職業ではありません。礼節の型を身につけ、現場の判断力を育て、やがて家や組織全体を見渡せる視座へ至る——その過程を、当協会は資格制度として可視化しています。ルートの中核は、Associate Butler（初級）、Professional Butler（中級）、Master Butler（上級）の三段階。これにワイン・紅茶・アフタヌーンティー・食卓演出・邸宅管理・旅程手配などの専門資格が横串として交わり、一人ひとりの適性と勤務先の要請に応じた「深み」を加えます。
              </p>
              <p>
                以下では、求められる素養、学びの入口、各級で問われること、専門資格の位置づけ、そして資格取得後の実務と継続研鑽までを順に案内します。まずは全体像を把握し、ご自身の現在地から次の一歩を選んでください。
              </p>
            </div>
            <ol className="mt-10 grid sm:grid-cols-3 border-t-2 border-[#1a2942]">
              {[
                { step: 'Step 01', label: '素養を知る', sub: '適性と心構え' },
                { step: 'Step 02', label: '学びを始める', sub: '通信教育と対面' },
                { step: 'Step 03', label: '級を登る', sub: '初級から上級へ' }
              ].map((s) => (
                <li key={s.step} className="border-b sm:border-b-0 sm:border-r last:border-r-0 border-[#e2ddd2] px-5 py-6">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{s.step}</span>
                  <span className="block jba-display font-medium text-[#1a2942] text-lg mb-1">{s.label}</span>
                  <span className="block font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]">{s.sub}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* S2. 素養と適性 — 4枚白リッチカード＋画像（番号2カラム事業領域とは別型） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              II. Aptitude
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              求められる素養と適性
            </h2>
            <div className="grid lg:grid-cols-[1fr_340px] gap-10 items-start mb-10">
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                <p>
                  技術は後から学べます。一方で、人の気配に敏感であること、自分を前に出しすぎないこと、約束を黙って守ることは、日々の姿勢としてすでに芽生えている必要があります。当協会が重視するのは、華やかな自己主張ではなく、主人と家族の時間を守り、場を滑らかにする静かな意志です。
                </p>
                <p>
                  また、学び直しを厭わない柔軟さも適性の一部です。生活様式や道具は時代とともに変わりますが、「先回りの奉仕」という核は変わりません。古い型を尊びつつ、いまの暮らしに合わせて手順を更新できる人ほど、長く信頼される執事へと育っていきます。
                </p>
              </div>
              <img
                src="/images/generated/jba-butler-team-briefing.jpg"
                alt="研修前に打ち合わせを行う執事たち。観察と段取りを共有する場面"
                className="w-full h-64 lg:h-[300px] object-cover border border-[#a38e65]/30"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {aptitudePillars.map((p) => (
                <div key={p.en} className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] px-6 py-7">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.08em] mb-2">{p.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-3">{p.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{p.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* S3. 学びの入口 — 紺帯番号バンド＋二分割パネル */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. Gateways to Learning
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              学びの入口 — 通信教育と対面研修
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              入口は一つではありません。理論を先に固める方、身体感覚から入る方、双方を往復する方。通信教育プログラムと対面研修は対立するものではなく、互いの不足を補う両輪として設計されています。
            </p>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-4 mb-8 flex flex-wrap items-center gap-x-8 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">LEARNING PATH</span>
              <span className="font-['Noto_Serif_JP',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                通信で思想を固め、対面で所作を磨く
              </span>
            </div>
            <div className="grid lg:grid-cols-2 gap-8">
              {learningEntries.map((e) => (
                <div key={e.no} className="border border-[#e2ddd2] bg-[#fbfaf7] p-7 sm:p-8 flex flex-col">
                  <div className="flex items-baseline gap-4 mb-4">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none">{e.no}</span>
                    <h3 className="jba-display font-medium text-[#1a2942] text-xl">{e.title}</h3>
                  </div>
                  <p className="font-['Noto_Serif_JP',serif] text-[#8f7a56] text-sm tracking-[0.04em] mb-3">{e.lead}</p>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] flex-1 mb-6">{e.body}</p>
                  <a href={e.href} className={BTN08_ON_LIGHT}>
                    {e.linkLabel}
                  </a>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <img
                src="/images/generated/jba-become-foundation-training.jpg"
                alt="基礎研修の様子。礼節と所作を丁寧に学ぶ場面"
                className="w-full h-56 sm:h-72 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S4. 三段階資格 — Associate / Professional / Master を統合（各級見出しはh3へ降格） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IV. Three Grades
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              三段階の資格で問われること
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10 max-w-3xl">
              Associate Butler（初級）、Professional Butler（中級）、Master Butler（上級）は、礼節の型から現場判断、そして家や組織全体を導く責任へと視座を広げる連続した道筋です。各級で問われる力を順に見ていきます。
            </p>

            {/* Associate */}
            <div className="mb-14 sm:mb-16">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                Associate Butler
              </span>
              <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl leading-[1.5] tracking-[0.02em] mb-5">
                Associate Butler（初級）で問われること
              </h3>
              <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
                <div>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-6">
                    初級は、「執事として現場に立つための共通言語」を身につける段階です。華やかな演出よりも、挨拶、取次、身だしなみ、指示の受け方、守秘の意識といった基礎を、抜け漏れなく定着させます。ここでは速さより正確さ、自己流より型の理解が評価の中心になります。
                  </p>
                  <ul className="border-t border-[#1a2942]">
                    {associatePoints.map((line) => (
                      <li
                        key={line}
                        className="flex gap-4 border-b border-[#e2ddd2] py-4 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] leading-[1.85]"
                      >
                        <span className="text-[#a38e65] shrink-0 mt-0.5">▸</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    <a href="/qualifications/associate-butler" className={BTN08_ON_LIGHT}>
                      Associate Butler の詳細
                    </a>
                  </div>
                </div>
                <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
                  <p className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm mb-3">Foundation first</p>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] mb-6">
                    初級で培った型は、中級以降の判断を支える骨格になります。焦って上級領域に手を伸ばすより、基本動作を美しく再現できる状態をつくることが、長い職業人生では結果的に近道です。
                  </p>
                  <img
                    src="/images/generated/jba-butler-quality-control.jpg"
                    alt="サービスの質を点検する手元。正確さと再現性を重んじる初級の精神"
                    className="w-full h-52 object-cover border border-[#a38e65]/30"
                  />
                </div>
              </div>
            </div>

            {/* Professional */}
            <div className="mb-14 sm:mb-16">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                Professional Butler
              </span>
              <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl leading-[1.5] tracking-[0.02em] mb-5">
                Professional Butler（中級）で問われること
              </h3>
              <div className="grid lg:grid-cols-[320px_1fr] gap-10 items-start">
                <img
                  src="/images/generated/jba-become-professional-practice.jpg"
                  alt="実務の現場で動線と段取りを確認する中級執事のイメージ"
                  className="w-full h-64 lg:h-full min-h-[260px] object-cover border border-[#a38e65]/30"
                />
                <div>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-6">
                    中級では、指示待ちから一歩踏み出し、状況を読んで優先順位を付けられるかが問われます。食卓の流れ、来客対応、出張や行事の準備、外部パートナーとの調整——複数の糸を同時に手に持ちながら、主人の前ではあくまで静かでいる。その両立がProfessionalの中核です。
                  </p>
                  <div className="grid sm:grid-cols-1 gap-0 border-t border-[#e2ddd2]">
                    {professionalPoints.map((line, idx) => (
                      <div key={line} className="flex gap-5 py-4 border-b border-[#e2ddd2]">
                        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl shrink-0 w-8">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] leading-[1.85]">{line}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8">
                    <a href="/qualifications/professional-butler" className={BTN08_ON_LIGHT}>
                      Professional Butler の詳細
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Master */}
            <div>
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                Master Butler
              </span>
              <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl leading-[1.5] tracking-[0.02em] mb-6">
                Master Butler（上級）で問われること
              </h3>
              <div className="bg-[#1a2942] p-8 sm:p-10 lg:p-12">
                <div className="grid lg:grid-cols-[1fr_300px] gap-10 items-start">
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] mb-8">
                      上級は、個人の技能の頂点であると同時に、後進と家全体を導く責任の段階です。自らが最前線で完璧に動くこと以上に、基準を示し、人を育て、非常時にも方針を見失わないことが求められます。Master Butlerは、協会が掲げる執事像を現場で体現する存在として位置づけられます。
                    </p>
                    <ul className="space-y-4">
                      {masterPoints.map((line) => (
                        <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#f8f6f0] leading-[1.85]">
                          <span className="text-[#a38e65] shrink-0">—</span>
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-10">
                      <a href="/qualifications/master-butler" className={BTN08_ON_NAVY}>
                        Master Butler の詳細
                      </a>
                    </div>
                  </div>
                  <img
                    src="/images/generated/jba-become-master-leadership.jpg"
                    alt="後進を導く上級執事のリーダーシップを想起させる佇まい"
                    className="w-full h-56 lg:h-[340px] object-cover border border-[#a38e65]/40"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* S5. 専門資格 — グリッド（事業領域2カラムともタイムラインとも異なるタイル型） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Specialist Credentials
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              専門資格の位置づけ
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8">
              <p>
                三段階の本線資格が「執事としての総合力」を示すのに対し、専門資格は特定領域の深度を証明します。ワインや紅茶、アフタヌーンティー、食卓演出、邸宅管理、旅程手配、ビジネスマナー、ユニフォームやイベント企画——いずれも、主人のライフスタイルに直結するテーマです。
              </p>
              <p>
                専門資格は本線の代替ではありません。Associateで型を得たあと、勤務先の要請や自身の関心に合わせて横断的に取得する「横の拡張」として機能します。Professional以降の現場では、これら専門知を日常のオペレーションへ自然に溶かす統合力が問われます。
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#e2ddd2] border border-[#e2ddd2]">
              {specialtyQuals.map((q) => (
                <a
                  key={q.href}
                  href={q.href}
                  className="bg-white px-5 py-6 hover:bg-[#f8f6f0] transition-colors group"
                >
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">
                    {q.en}
                  </span>
                  <span className="jba-display font-medium text-[#1a2942] text-base group-hover:text-[#a38e65] transition-colors">
                    {q.ja}
                  </span>
                </a>
              ))}
            </div>
            <p className="mt-6 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">
              各専門資格の到達目標と学びの内容は、資格制度の個別ページで確認できます。まずは
              <a href="/qualifications" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">
                資格制度の一覧
              </a>
              から全体を俯瞰することをおすすめします。
            </p>
          </section>

          {/* S6. 実務と継続研鑽 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VI. Practice & Continuance
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              資格取得後の実務と継続研鑽
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10">
              <p>
                資格を手にした瞬間からが、本当の実務の始まりです。家庭に入るにせよ、法人の接遇や邸宅管理に関わるにせよ、相手の流儀を学び、自分の型をその家に合わせて調整する作業が続きます。協会が授与する資格は、能力の証明であると同時に、「品質を落とさない」という社会への約束でもあります。
              </p>
              <p>
                継続研鑽の形は人それぞれです。専門資格の追加、通信教育での理論の再確認、同僚との事例共有、主人からのフィードバックの記録——いずれも、見えない奉仕の精度を高める習慣になります。会員制度を通じた学びのコミュニティに参加することも、孤立しがちな現場職を支える有効な手段です。
              </p>
              <p>
                重ねて強調したいのは、当協会の資格ルートが年齢・性別・学歴による制限を設けていないという点です。人生のどの時点からでも、奉仕の道は開けます。大切なのは、今日の自分に必要な学びを選び、着実に級を積み上げることです。
              </p>
            </div>
            <dl className="grid sm:grid-cols-3 gap-6 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-2">実務の場</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] leading-[1.85]">
                  個人宅・法人・催事など、資格と適性に応じた現場で「先回りの奉仕」を実装します。
                </dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-2">研鑽の循環</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] leading-[1.85]">
                  現場の気づきを学びへ戻し、専門資格や再学習で理論を更新する循環を保ちます。
                </dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-2">開かれた入口</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] leading-[1.85]">
                  年齢・性別・学歴を問わず、意志ある方に資格ルートを開いています。
                </dd>
              </div>
            </dl>
          </section>

          {/* S7. FAQ — アコーディオン風の細罫Q&A（新バリエーション） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Questions
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある問い
            </h2>
            <div className="border-t-2 border-[#1a2942]">
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* S8. 導線CTA */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. Next Steps
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              次の一歩を選ぶ
            </h2>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                執事という仕事の背景を知る、資格の詳細を比較する、学びの共同体に触れる——関心の近い扉からお進みください。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、いまこの時代にふさわしい執事の育成を続けています。
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/butler" className={BTN08_ON_NAVY}>
                  執事という仕事を知る
                </a>
                <a href="/qualifications/distance-learning" className={BTN08_ON_NAVY}>
                  通信教育から始める
                </a>
                <a href="/membership" className={BTN08_ON_NAVY}>
                  会員制度を見る
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
